import type { ToastProps } from '@nuxt/ui';
import { useToast } from '@nuxt/ui/composables';
import { useTimeoutFn } from '@vueuse/core';
import { mergeUi } from '#layers/ui/utils/merge-ui';

/** Flash guard: an operation that finishes quickly must never blink a toast. */
const PROGRESS_TOAST_DELAY_MS = 250;

export interface ProgressToastHandle {
	stop: () => void;
}

export function useProgressToast() {
	const toaster = useToast();

	function start(toast: Partial<ToastProps>): ProgressToastHandle {
		let toastId: string | number | null = null;

		const { start: startTimer, stop: stopTimer } = useTimeoutFn(() => {
			const { id } = toaster.add({
				color: 'neutral',
				close: false,
				duration: Number.POSITIVE_INFINITY,
				progress: false,
				icon: 'lucide:loader-circle',
				...toast,
				ui: mergeUi(toast.ui, { icon: 'animate-spin' }),
			});

			toastId = id;
		}, PROGRESS_TOAST_DELAY_MS, { immediate: false });

		startTimer();

		function stop() {
			stopTimer();

			if (toastId === null) return;

			toaster.remove(toastId);
			toastId = null;
		}

		return { stop };
	}

	return { start };
}
